import React from "react";
import { User } from "@/pages/auth/types";
import { LoadingState } from "@/components/employee/LoadingState";
import { ErrorState } from "@/components/employee/ErrorState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FileText,
  Check,
  AlertTriangle,
  Briefcase,
  Users,
  Award,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { Position } from "@/services/positionService";

export default function EmployeeCertifications() {
  const { toast } = useToast();

  // Fetch user data
  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      const response = await axiosInstance.get<User>(`user/me`);
      return response.data;
    },
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load your profile",
          variant: "destructive",
        });
      },
    },
  });

  // Fetch all positions
  const {
    data: allPositions,
    isLoading: positionsLoading,
    error: positionsError,
  } = useQuery({
    queryKey: ["positions"],
    queryFn: async () => {
      const response = await axiosInstance.get<Position[]>("position");
      return response.data;
    },
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load positions",
          variant: "destructive",
        });
      },
    },
  });

  // Use fetched user data
  const firstName = userData?.firstName;
  const lastName = userData?.lastName;
  const eligiblePositionIds = userData?.eligiblePositionIds ?? [];
  const positionDocuments = userData?.positionDocuments ?? [];
  const taxDocumentsSigned = userData?.taxDocumentsSigned ?? false;
  const isLoading = userLoading || positionsLoading;
  const error = userError ?? positionsError;

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState />;
  }

  // Create enriched position data with user certification and document status
  const enrichedPositions = (allPositions || []).map((position) => {
    const isEligible = eligiblePositionIds.includes(position.id);

    // Find position document info from user data
    const positionDoc = positionDocuments.find(
      (doc) => doc.positionId === position.id,
    );
    const documentSigned = positionDoc?.documentSigned ?? false;

    return {
      ...position,
      isEligible,
      documentSigned,
      positionDoc, // Include the positionDoc for easier access in render
    };
  });

  // Calculate statistics
  const totalPositions = allPositions?.length ?? 0;
  const eligiblePositions = enrichedPositions.filter(
    (p) => p.isEligible,
  ).length;
  const documentSignedCount = enrichedPositions.filter(
    (p) => p.isEligible && p.documentSigned,
  ).length;
  const pendingDocuments = enrichedPositions.filter(
    (p) => p.isEligible && p.positionDoc && !p.documentSigned,
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/5 p-6 transition-colors">
      <div className="max-w-4xl mx-auto">
        <div className="bg-primary/5 backdrop-blur-sm rounded-xl p-8 mb-8 border border-border">
          <h1 className="mb-2 text-4xl font-bold text-foreground">
            Employee Certifications
          </h1>
          <p className="text-muted-foreground mb-4">
            View your qualification status and document requirements
          </p>

          <div className="flex flex-col md:flex-row items-center mb-4 gap-4">
            <div className="w-full py-4 px-6 backdrop-blur-md bg-white/5 rounded-lg border border-border flex flex-row justify-between items-center">
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground">
                  {firstName} {lastName}
                </h2>
                <p className="text-muted-foreground text-sm">
                  Certification Status
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground">Tax Documents:</span>
                {taxDocumentsSigned ? (
                  <div className="flex items-center gap-1 text-success bg-success/10 py-1 px-3 rounded-full">
                    <Check className="w-4 h-4" />
                    <span className="text-xs font-medium">Completed</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-warning bg-warning/10 py-1 px-3 rounded-full">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-xs font-medium">Pending</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <div className="bg-white/5 backdrop-blur-md rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    Total Positions
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {totalPositions}
                  </p>
                </div>
                <Briefcase className="w-8 h-8 text-foreground" />
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Eligible</p>
                  <p className="text-2xl font-bold text-info">
                    {eligiblePositions}
                  </p>
                </div>
                <Award className="w-8 h-8 text-info" />
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">
                    Documents Signed
                  </p>
                  <p className="text-2xl font-bold text-success">
                    {documentSignedCount}
                  </p>
                </div>
                <Check className="w-8 h-8 text-success" />
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Pending Docs</p>
                  <p className="text-2xl font-bold text-warning">
                    {pendingDocuments}
                  </p>
                </div>
                {pendingDocuments === 0 ? (
                  <Check className="w-8 h-8 text-success" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-warning" />
                )}
              </div>
            </div>
          </div>

          {/* Positions List */}
          <Card className="bg-secondary/10 backdrop-blur-sm border border-border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Position Certifications & Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {enrichedPositions.map((position) => (
                  <div
                    key={position.id}
                    className="flex items-center justify-between p-4 rounded-lg transition-colors bg-white/5 border border-border hover:bg-white/10"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground mb-1">
                        {position.tableName}
                      </h3>
                      {position.description && (
                        <p className="text-sm text-muted-foreground">
                          {position.description}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {/* Certification Status */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground">
                          Certified:
                        </span>
                        {position.isEligible ? (
                          <div className="flex items-center gap-1 text-success bg-success/10 py-1 px-3 rounded-full">
                            <Check className="w-4 h-4" />
                            <span className="text-xs font-medium">Yes</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-muted-foreground bg-muted/10 py-1 px-3 rounded-full">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs font-medium">No</span>
                          </div>
                        )}
                      </div>

                      {/* Document Status - Show if user is eligible and there's a position document */}
                      {position.isEligible && position.positionDoc && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            Document:
                          </span>
                          {position.documentSigned ? (
                            <div className="flex items-center gap-1 text-success bg-success/10 py-1 px-3 rounded-full">
                              <Check className="w-4 h-4" />
                              <span className="text-xs font-medium">
                                Signed
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-warning bg-warning/10 py-1 px-3 rounded-full">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-xs font-medium">
                                Pending
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* No document required indicator - Show if eligible but no position document exists */}
                      {position.isEligible && !position.positionDoc && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            Document:
                          </span>
                          <div className="flex items-center gap-1 text-muted-foreground bg-muted/10 py-1 px-3 rounded-full">
                            <span className="text-xs font-medium">
                              Not Required
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
